// 採購 — 採購單 (draft → 落單 → 部分 / 全部到貨 → 關閉), 供應商, and 建議採購
// from stock + last-30-day sales. Every PO change goes through the pos_*_po
// RPCs in supabase/migrations/pos_phase3.sql; receiving updates stock + the
// weighted-average cost server-side, so afterwards we just ask the parent to
// reload products (onStockChanged). HKD only, no supplier payments.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { zhEn, dateLocale } from "./posTier.js";
import {
  Search, ScanLine, Plus, Trash2, Pencil, Phone, Mail, MessageCircle, Printer, Send, PackageCheck, Undo2,
  Truck, ClipboardList, Lightbulb, Lock, XCircle, CheckSquare, Square, ChevronRight, RotateCcw,
 Sparkles,
} from "lucide-react";
import { C, Btn, Chip, Pill, Seg, Card, Sheet, Label, Field, Toggle, Stepper, Empty, money, round2, inputStyle, useWide } from "./posUi.jsx";
import { posErrorText, newId } from "./posMath.js";
import { ProductThumb } from "./Register.jsx";
import {
  fetchSuppliers, saveSupplier, deleteSupplier, fetchPurchaseOrders, savePurchaseOrder, setPurchaseOrderStatus,
  receivePurchaseOrder, returnPurchaseOrderLine, fetchSalesVelocity, bulkPatchPosProducts, posAi, fileForAi, setSupplierCode,
} from "../../lib/api.js";

export const PO_STATUS = {
  draft: [zhEn("草稿", "Draft"), "grey"], pending: [zhEn("待到貨", "Awaiting delivery"), "blue"], partial: [zhEn("部分到貨", "Part received"), "amber"],
  received: [zhEn("已到貨", "Received"), "green"], closed: [zhEn("已關閉", "Closed"), "grey"], cancelled: [zhEn("已取消", "Cancelled"), "red"],
};
const OPEN = ["draft", "pending", "partial"];
const REASONS = [zhEn("發錯貨", "Wrong item sent"), zhEn("破損", "Damaged"), zhEn("過期", "Expired"), zhEn("規格不符", "Not as specified"), zhEn("數量短少", "Short quantity"), zhEn("其他", "Other")];

const PO_ERR = [
  ["qty_below_received", zhEn("數量唔可以少過已收貨嘅數量", "The quantity can't be less than what's already received")],
  ["cannot_remove_received_line", zhEn("已經收過貨嘅項目唔可以刪除", "A line that has been received can't be deleted")],
  ["po_locked", zhEn("呢張採購單已完成 / 關閉，唔可以再改", "This purchase order is completed / closed and can't be changed")],
  ["po_money_locked", zhEn("已經有貨到咗，唔可以再改折扣、運費、稅或者已到貨項目嘅成本", "Stock has already arrived, so discount, shipping, tax and the cost of received lines can't be changed")],
  ["po_needs_supplier", zhEn("落單之前要揀供應商", "Choose a supplier before placing the order")],
  ["bad_transition", zhEn("而家嘅狀態唔可以咁做（可能已經收咗貨），請重新整理", "That can't be done in the current state (goods may already have been received) — please refresh")],
  ["over_receive", zhEn("收貨數量多過訂購數量", "Received quantity is more than ordered")],
  ["qty_exceeds_received", zhEn("退貨數量多過已收（未退）嘅數量", "Return quantity is more than what was received (and not yet returned)")],
  ["po_not_found", zhEn("搵唔到採購單（可能已刪除）", "Purchase order not found (it may have been deleted)")],
  ["line_not_found", zhEn("搵唔到呢個項目，請重新整理", "Line not found — please refresh")],
];
const poErr = (e) => { const m = String(e?.message || e || ""); for (const [k, v] of PO_ERR) if (m.includes(k)) return v; return posErrorText(e); };

// ── small helpers ──
const pad = (n) => String(n).padStart(2, "0");
const isoDay = (d) => d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
const todayIso = () => isoDay(new Date());
const addDays = (iso, n) => { const d = new Date((iso || todayIso()) + "T00:00:00"); d.setDate(d.getDate() + Number(n || 0)); return isoDay(d); };
const fmtDay = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return +d + "/" + +m + (y !== String(new Date().getFullYear()) ? "/" + y.slice(2) : "");
};
export const poNo = (n) => "PO-" + String(n ?? "").padStart(4, "0");
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const qtyTxt = (n) => String(round2(n));
const waDigits = (phone) => { const d = String(phone || "").replace(/\D/g, ""); return d.length === 8 ? "852" + d : (d.length === 11 && d[0] === "1") ? "86" + d : d; };
const sortLines = (po) => [...(po?.purchase_order_lines || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
const poQty = (po) => sortLines(po).reduce((s, l) => s + num(l.qty) + num(l.free_qty), 0);
const poGot = (po) => sortLines(po).reduce((s, l) => s + num(l.received_qty), 0);
// Sort helper that copes with Infinity (no sales) on both sides.
const byDays = (a, b) => (a === b ? 0 : a === Infinity ? 1 : b === Infinity ? -1 : a - b);
const isOverdue = (po, today) => (po.status === "pending" || po.status === "partial") &&
  (po.expected_date ? po.expected_date < today : addDays(po.order_date, 7) < today);

// Same maths as pos_po_totals — the server recomputes and stores these.
export function poTotals({ lines, discount, discountType, shippingFee, taxRate }) {
  const sub = (lines || []).reduce((s, l) => s + Math.max(0, num(l.cost) * num(l.qty) - num(l.discount)), 0);
  const d = Math.max(0, num(discount));
  const disc = Math.min(sub, discountType === "percent" ? round2(sub * d / 100) : d);
  const afterDisc = sub - disc;
  const tax = round2(afterDisc * Math.max(0, num(taxRate)) / 100);
  const ship = Math.max(0, num(shippingFee));
  return { subtotal: round2(sub), discount: round2(disc), tax, shipping: ship, total: round2(afterDisc * (1 + Math.max(0, num(taxRate)) / 100) + ship) };
}
const rawTotals = (po) => poTotals({ lines: sortLines(po), discount: po.discount, discountType: po.discount_type, shippingFee: po.shipping_fee, taxRate: po.tax_rate });

const productMatches = (p, s, supplierId) => {
  const code = supplierId ? String(p.supplierCodes?.[supplierId] || "").toLowerCase() : "";
  return p.name.toLowerCase().includes(s) || p.sku.toLowerCase().includes(s) || p.barcode === s || (code && code.includes(s));
};
const findByCode = (list, code, supplierId) => {
  const c = String(code || "").trim();
  if (!c) return undefined;   // empty search / blank scan must not match a product with no barcode
  return list.find(p => p.barcode && p.barcode === c) || list.find(p => p.sku && p.sku === c) ||
    list.find(p => Object.entries(p.supplierCodes || {}).some(([sid, v]) => v && v === c && (!supplierId || sid === supplierId)));
};

export default function Purchasing({ tenant, sym = "$", products = [], setProducts, onScanOpen, toast, onStockChanged }) {
  const wide = useWide(900);
  const [view, setView] = useState("po");
  const [suppliers, setSuppliers] = useState(null);
  const [pos, setPos] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [editor, setEditor] = useState(null);     // { po } | { seed }
  const [aiDoc, setAiDoc] = useState(null);       // {busy, err}
  // 🤖 Supplier invoice / PO / quote (photo or PDF) → a prefilled PO editor.
  // Lines for the same product (paid + 贈品) are merged; unknown items stay
  // as named lines the shop can match or leave.
  const scanDoc = async (file) => {
    setAiDoc({ busy: true });
    try {
      const doc = await fileForAi(file, 2000);
      const res = await posAi({ task: "po_from_document", tenantId: tenant.id, file: doc });
      const po = res?.po || {};
      const lines = [];
      for (const l of po.lines || []) {
        const prod = l.matchId ? products.find(x => x.id === l.matchId) : null;
        const same = prod && lines.find(x => x.productId === prod.id);
        if (same) {
          // Paid + 贈品 lines of one product merge; the invoice's paid cost wins
          // and line discounts add up.
          same.qty = round2(same.qty + (l.qty || 0)); same.freeQty = round2(same.freeQty + (l.freeQty || 0));
          if (l.qty > 0 && l.unitCost) same.cost = String(l.unitCost);
          if (l.discount) same.discount = String(round2(num(same.discount) + l.discount));
          continue;
        }
        const id = newId();
        lines.push({
          key: id, id, productId: prod?.id || null, name: prod?.name || l.name, sku: prod?.sku || l.code || "",
          qty: round2(l.qty || 0), freeQty: round2(l.freeQty || 0), cost: l.qty > 0 ? String(l.unitCost || (prod?.cost ?? "")) : String(prod?.cost ?? l.unitCost ?? ""),
          discount: l.discount ? String(l.discount) : "", expiryDate: "", received: 0,
          supplierCode: l.code || "", aiName: l.name, aiUnmatched: !prod,
        });
      }
      setAiDoc(null);
      setEditor({ seed: {
        supplierId: po.supplierId || "", supplierRef: po.supplierRef || "", orderDate: po.date || "", lines,
        discount: po.discount || "", shippingFee: po.shipping || "",
        aiNote: { supplierName: po.supplier, currency: po.currency, unmatched: lines.filter(x => x.aiUnmatched).length, total: lines.length },
      } });
    } catch (e) { setAiDoc({ busy: false, err: e?.message || String(e) }); }
  };
  const [detailId, setDetailId] = useState(null);
  const [supEdit, setSupEdit] = useState(null);   // supplier row | {} for new
  const [msg, setMsg] = useState("");

  const flash = useCallback((m, type) => {
    if (toast) toast(m, type);
    else { setMsg(m); setTimeout(() => setMsg(""), 2400); }
  }, [toast]);

  const loadPos = useCallback(async () => {
    const rows = await fetchPurchaseOrders(tenant.id, 300);
    setPos(rows);
    return rows;
  }, [tenant.id]);
  const load = useCallback(async () => {
    setLoadErr("");
    try {
      const [s, p] = await Promise.all([fetchSuppliers(tenant.id), fetchPurchaseOrders(tenant.id, 300)]);
      setSuppliers(s); setPos(p);
    } catch (e) { setLoadErr(posErrorText(e)); setSuppliers(x => x || []); setPos(x => x || []); }
  }, [tenant.id]);
  useEffect(() => { load(); }, [load]);

  // Variant parents aren't stocked themselves — buy the variants.
  const buyable = useMemo(() => {
    const parents = new Set(products.map(p => p.parentId).filter(Boolean));
    return products.filter(p => !parents.has(p.id));
  }, [products]);
  const supById = useMemo(() => Object.fromEntries((suppliers || []).map(s => [s.id, s])), [suppliers]);

  const onSupplierSaved = (row) => setSuppliers(ss => [...(ss || []).filter(s => s.id !== row.id), row].sort((a, b) => a.name.localeCompare(b.name)));
  const afterStock = async () => { try { await loadPos(); } catch { /* list refresh only */ } onStockChanged?.(); };

  if (suppliers === null || pos === null) return <p style={{ textAlign: "center", color: C.sub, fontWeight: 800, padding: 40 }}>{zhEn("載入中…", "Loading…")}</p>;

  const detail = detailId ? pos.find(p => p.id === detailId) : null;
  return (
    <div>
      <Seg value={view} onChange={setView} style={{ marginBottom: 12 }} options={[["po", zhEn("採購單", "Purchase orders")], ["suppliers", zhEn("供應商", "Suppliers")], ["suggest", zhEn("建議採購", "Suggested")]]} />
      {loadErr && <p style={{ color: C.red, fontWeight: 800, fontSize: 13 }}>{zhEn("載入失敗：", "Failed to load: ")}{loadErr} <button type="button" onClick={load} style={{ border: "none", background: "none", color: C.accent, fontWeight: 900, cursor: "pointer", fontFamily: "inherit" }}>{zhEn("再試", "Retry")}</button></p>}

      {view === "po" && <PoList pos={pos} supById={supById} sym={sym} wide={wide} onOpen={setDetailId} onNew={() => setEditor({ seed: {} })} onScanDoc={scanDoc} />}
      <Sheet open={!!aiDoc} onClose={() => !aiDoc?.busy && setAiDoc(null)} title="">
        <div style={{ textAlign: "center", padding: "20px 6px 10px" }}>
          {aiDoc?.busy ? <>
            <div style={{ width: 64, height: 64, margin: "0 auto 14px", borderRadius: 99, background: C.grad, display: "flex", alignItems: "center", justifyContent: "center" }}><Sparkles size={30} color="#fff" /></div>
            <p style={{ fontWeight: 900, color: C.ink, fontSize: 17, margin: 0 }}>{zhEn("AI 讀緊發票…", "AI is reading the invoice…")}</p>
            <p style={{ fontWeight: 700, color: C.sub, fontSize: 13, margin: "6px 0 0" }}>{zhEn("逐行配對你嘅商品，大約 15–30 秒", "Matching each line to your products — about 15–30 seconds")}</p>
          </> : <>
            <p style={{ fontSize: 40, margin: 0 }}>😵</p>
            <p style={{ fontWeight: 700, color: C.red, fontSize: 13, margin: "6px 0 14px" }}>{aiDoc?.err}</p>
            <Btn kind="ghost" onClick={() => setAiDoc(null)}>{zhEn("關閉", "Close")}</Btn>
          </>}
        </div>
      </Sheet>
      {view === "suppliers" && <SupplierList suppliers={suppliers} pos={pos} products={products} wide={wide} onEdit={setSupEdit}
        onNewPo={(s) => setEditor({ seed: { supplierId: s.id } })} />}
      {view === "suggest" && <Suggest tenant={tenant} sym={sym} products={buyable} pos={pos} supById={supById} wide={wide} flash={flash}
        onCreated={async () => { try { await loadPos(); } catch { /* */ } }} onOpenPo={(id) => { setView("po"); setDetailId(id); }} />}

      {detail && <PoDetail po={detail} supplier={supById[detail.supplier_id]} products={products} tenant={tenant} sym={sym} flash={flash} onScanOpen={onScanOpen}
        onClose={() => setDetailId(null)} onEdit={() => { setEditor({ po: detail }); setDetailId(null); }}
        onChanged={async (stock) => { if (stock) await afterStock(); else { try { await loadPos(); } catch { /* */ } } }} />}

      {editor && <PoEditor onProductsLinked={(rows) => { const byId = Object.fromEntries(rows.map(r => [r.id, r])); setProducts?.(ps => ps.map(p => byId[p.id] ? { ...p, ...(byId[p.id].supplierId !== undefined ? { supplierId: byId[p.id].supplierId } : null), ...(byId[p.id].supplierCodes ? { supplierCodes: byId[p.id].supplierCodes } : null) } : p)); }} tenant={tenant} sym={sym} po={editor.po} seed={editor.seed} suppliers={suppliers} supById={supById} products={buyable}
        onScanOpen={onScanOpen} onSupplierSaved={onSupplierSaved} onClose={() => setEditor(null)}
        onSaved={async (row) => { setEditor(null); flash(poNo(row.number) + zhEn(" 已儲存", " saved")); try { await loadPos(); } catch { /* */ } setView("po"); setDetailId(row.id); }} />}

      {supEdit && <SupplierSheet tenant={tenant} supplier={supEdit} products={products} pos={pos} onClose={() => setSupEdit(null)}
        onSaved={(row) => { onSupplierSaved(row); setSupEdit(null); flash(zhEn("已儲存供應商", "Supplier saved")); }}
        onDeleted={(id) => {
          setSuppliers(ss => ss.filter(s => s.id !== id));
          setPos(ps => ps.map(p => p.supplier_id === id ? { ...p, supplier_id: null } : p));
          setProducts?.(ps => ps.map(p => p.supplierId === id ? { ...p, supplierId: null } : p));
          setSupEdit(null); flash(zhEn("已刪除供應商", "Supplier deleted"));
        }} />}

      {msg && <div style={{ position: "fixed", left: "50%", bottom: 90, transform: "translateX(-50%)", zIndex: 950, background: "rgba(30,27,52,0.9)", color: "#fff", padding: "10px 18px", borderRadius: 99, fontWeight: 800, fontSize: 14 }}>{msg}</div>}
    </div>
  );
}

// ── 採購單 list ─────────────────────────────────────────────────────────────
function PoList({ pos, supById, sym, wide, onOpen, onNew, onScanDoc }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("open");
  const today = todayIso();
  const kpi = useMemo(() => ({
    draft: pos.filter(p => p.status === "draft").length,
    waiting: pos.filter(p => p.status === "pending" || p.status === "partial").length,
    late: pos.filter(p => isOverdue(p, today)).length,
  }), [pos, today]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return pos.filter(p => {
      if (filter === "open" && !OPEN.includes(p.status)) return false;
      if (filter === "late" && !isOverdue(p, today)) return false;
      if (filter === "waiting" && !(p.status === "pending" || p.status === "partial")) return false;
      if (!["open", "late", "waiting", "all"].includes(filter) && p.status !== filter) return false;
      if (!s) return true;
      const hay = [poNo(p.number), "#" + p.number, String(p.number), p.supplier_ref, supById[p.supplier_id]?.name || "",
        ...sortLines(p).map(l => l.name + " " + l.sku)].join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [pos, q, filter, supById, today]);

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 12 }}>
        <Stat label={zhEn("草稿", "Draft")} value={kpi.draft} onClick={() => setFilter("draft")} />
        <Stat label={zhEn("待到貨", "Awaiting delivery")} value={kpi.waiting} tone={kpi.waiting ? C.blue : undefined} onClick={() => setFilter("waiting")} />
        <Stat label={zhEn("逾期", "Overdue")} value={kpi.late} tone={kpi.late ? C.red : undefined} onClick={() => setFilter("late")} />
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, position: "relative" }}>
          <Search size={18} color={C.faint} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={zhEn("搜尋單號、供應商或貨品", "Search order number, supplier or item")} style={{ ...inputStyle, paddingLeft: 38, borderRadius: 14 }} />
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 16, padding: "0 12px", background: "linear-gradient(135deg,#7C3AED,#FF5C8A)", color: "#fff", fontWeight: 900, fontSize: 14, cursor: "pointer", whiteSpace: "nowrap" }}>
          <input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onScanDoc?.(f); }} />
          <Sparkles size={16} /> {zhEn("AI 掃描", "AI scan")}
        </label>
        <Btn onClick={onNew} style={{ padding: "0 14px" }}><Plus size={18} /> {zhEn("新增", "Add")}</Btn>
      </div>
      <p style={{ fontSize: 12, fontWeight: 700, color: C.sub, margin: "-4px 2px 10px" }}>{zhEn("🤖 影低供應商發票 / 送貨單 / 報價單（相或 PDF），AI 自動開好採購單俾你核對", "🤖 Photograph a supplier invoice / delivery note / quote (photo or PDF) and AI drafts the purchase order for you to check")}</p>
      <div className="pos-noscroll" style={{ display: "flex", gap: 6, marginBottom: 10, overflowX: "auto" }}>
        {[["open", zhEn("未完成", "Open")], ["draft", zhEn("草稿", "Draft")], ["waiting", zhEn("待到貨", "Awaiting delivery")], ["late", zhEn("逾期", "Overdue")], ["received", zhEn("已到貨", "Received")], ["closed", zhEn("已關閉", "Closed")], ["cancelled", zhEn("已取消", "Cancelled")], ["all", zhEn("全部", "All")]]
          .map(([k, l]) => <Chip key={k} on={filter === k} onClick={() => setFilter(k)}>{l}</Chip>)}
      </div>
      {list.length === 0
        ? <Empty icon="🧾" title={pos.length ? zhEn("冇符合嘅採購單", "No matching purchase orders") : zhEn("未有採購單", "No purchase orders yet")} sub={pos.length ? "" : zhEn("開張採購單向供應商訂貨，到貨時一撳就入庫存", "Create a purchase order to order from a supplier; when it arrives, one tap adds it to stock")}
            action={!pos.length && <Btn onClick={onNew}><Plus size={18} /> {zhEn("新增採購單", "New purchase order")}</Btn>} />
        : (
          <div style={{ display: "grid", gridTemplateColumns: wide ? "repeat(2,minmax(0,1fr))" : "minmax(0,1fr)", gap: 8 }}>
            {list.map(p => {
              const st = PO_STATUS[p.status] || [p.status, "grey"];
              const total = poQty(p), got = poGot(p), late = isOverdue(p, today);
              return (
                <Card key={p.id} onClick={() => onOpen(p.id)} style={{ padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 900, color: C.ink, fontSize: 15 }}>{poNo(p.number)}</span>
                    <Pill tone={st[1]}>{st[0]}</Pill>
                    {late && <Pill tone="red">{zhEn("逾期", "Overdue")}</Pill>}
                    <span style={{ flex: 1 }} />
                    <span style={{ fontWeight: 900, color: C.ink, fontSize: 16 }}>{money(p.total, sym)}</span>
                  </div>
                  <p style={{ margin: "4px 0 0", fontSize: 13.5, fontWeight: 800, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{supById[p.supplier_id]?.name || zhEn("未指定供應商", "No supplier")}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 12, fontWeight: 700, color: C.sub }}>
                    <span>{zhEn("落單", "Ordered")} {fmtDay(p.order_date)}</span>
                    <span style={{ color: late ? C.red : C.sub }}>{zhEn("到貨", "Due")} {fmtDay(p.expected_date)}</span>
                    <span style={{ flex: 1 }} />
                    {p.status !== "draft" && p.status !== "cancelled" && total > 0 && <span style={{ color: got >= total ? C.green : C.sub }}>{qtyTxt(got)}/{qtyTxt(total)} {zhEn("件", "items")}</span>}
                    {p.status === "draft" && <span>{sortLines(p).length} {zhEn("項", "lines")}</span>}
                  </div>
                  {p.status !== "draft" && total > 0 && got > 0 && got < total && (
                    <div style={{ height: 4, borderRadius: 99, background: C.line, marginTop: 6, overflow: "hidden" }}>
                      <div style={{ width: Math.min(100, got / total * 100) + "%", height: "100%", background: C.amber }} />
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
    </div>
  );
}

const Stat = ({ label, value, tone, onClick, sub }) => (
  <Card onClick={onClick} style={{ padding: "12px 14px" }}>
    <p style={{ margin: 0, fontSize: 12, fontWeight: 900, color: C.sub }}>{label}</p>
    <p style={{ margin: "2px 0 0", fontSize: 22, fontWeight: 900, color: tone || C.ink, letterSpacing: -0.5 }}>{value}</p>
    {sub && <p style={{ margin: 0, fontSize: 11.5, fontWeight: 700, color: C.sub }}>{sub}</p>}
  </Card>
);

// ── 供應商 ─────────────────────────────────────────────────────────────────
function SupplierList({ suppliers, pos, products, wide, onEdit, onNewPo }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return suppliers.filter(x => !s || [x.name, x.contact, x.phone, x.email].join(" ").toLowerCase().includes(s));
  }, [suppliers, q]);
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, position: "relative" }}>
          <Search size={18} color={C.faint} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={zhEn("搜尋供應商", "Search suppliers")} style={{ ...inputStyle, paddingLeft: 38, borderRadius: 14 }} />
        </div>
        <Btn onClick={() => onEdit({})} style={{ padding: "0 14px" }}><Plus size={18} /> {zhEn("新增", "Add")}</Btn>
      </div>
      {list.length === 0
        ? <Empty icon="🚚" title={suppliers.length ? zhEn("冇符合嘅供應商", "No matching suppliers") : zhEn("未有供應商", "No suppliers yet")} sub={suppliers.length ? "" : zhEn("加入供應商，記低聯絡、到貨日數同最低訂貨額", "Add suppliers to keep their contact, lead time and minimum order")}
            action={!suppliers.length && <Btn onClick={() => onEdit({})}><Plus size={18} /> {zhEn("新增供應商", "New supplier")}</Btn>} />
        : (
          <div style={{ display: "grid", gridTemplateColumns: wide ? "repeat(2,minmax(0,1fr))" : "minmax(0,1fr)", gap: 8 }}>
            {list.map(s => {
              const linked = products.filter(p => p.supplierId === s.id).length;
              const open = pos.filter(p => p.supplier_id === s.id && OPEN.includes(p.status)).length;
              const wa = waDigits(s.phone);
              return (
                <Card key={s.id} style={{ padding: "12px 14px" }}>
                  <div onClick={() => onEdit(s)} style={{ cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Truck size={18} color={C.accent} />
                      <span style={{ flex: 1, minWidth: 0, fontWeight: 900, fontSize: 15, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                      <Pencil size={15} color={C.faint} />
                    </div>
                    {(s.contact || s.email) && <p style={{ margin: "4px 0 0", fontSize: 12.5, fontWeight: 700, color: C.sub }}>{[s.contact, s.email].filter(Boolean).join(" · ")}</p>}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                      <Pill tone="grey">{s.lead_time_days ?? 7} {zhEn("日到貨", "days to deliver")}</Pill>
                      {num(s.moq_amount) > 0 && <Pill tone="amber">{zhEn("最低", "Min.")} {money(s.moq_amount)}</Pill>}
                      {num(s.moq_qty) > 0 && <Pill tone="amber">{zhEn("最少", "At least")} {qtyTxt(s.moq_qty)} {zhEn("件", "items")}</Pill>}
                      <Pill tone="pink">{linked} {zhEn("件貨品", "items")}</Pill>
                      {open > 0 && <Pill tone="blue">{open} {zhEn("張未完成", "open orders")}</Pill>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                    {s.phone && <a href={"tel:" + s.phone.replace(/[^\d+]/g, "")} style={linkBtn}><Phone size={15} /> {s.phone}</a>}
                    {wa && <a href={"https://wa.me/" + wa} target="_blank" rel="noreferrer" style={{ ...linkBtn, color: "#128C7E" }}><MessageCircle size={15} /> WhatsApp</a>}
                    <span style={{ flex: 1 }} />
                    <Btn kind="soft" small onClick={() => onNewPo(s)}><Plus size={15} /> {zhEn("採購單", "Purchase orders")}</Btn>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
    </div>
  );
}
const linkBtn = { display: "inline-flex", alignItems: "center", gap: 5, padding: "8px 11px", borderRadius: 12, background: "#F3F2F8", color: C.ink, fontWeight: 900, fontSize: 13, textDecoration: "none" };

function SupplierSheet({ tenant, supplier, products, pos, onClose, onSaved, onDeleted, z = 400 }) {
  const isNew = !supplier.id;
  const [f, setF] = useState(() => ({
    name: supplier.name || "", contact: supplier.contact || "", phone: supplier.phone || "", email: supplier.email || "",
    address: supplier.address || "", moq_amount: supplier.moq_amount ? String(Number(supplier.moq_amount)) : "",
    moq_qty: supplier.moq_qty ? String(Number(supplier.moq_qty)) : "", lead_time_days: String(supplier.lead_time_days ?? 7), notes: supplier.notes || "",
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    if (!f.name.trim()) { setErr(zhEn("請填供應商名稱", "Enter the supplier name")); return; }
    setBusy(true); setErr("");
    try {
      const row = await saveSupplier({
        id: supplier.id || newId(), tenant_id: tenant.id, name: f.name.trim(), contact: f.contact.trim(), phone: f.phone.trim(),
        email: f.email.trim(), address: f.address.trim(), moq_amount: Math.max(0, num(f.moq_amount)), moq_qty: Math.max(0, num(f.moq_qty)),
        lead_time_days: Math.max(0, Math.round(num(f.lead_time_days || 7))), notes: f.notes.trim(),
      });
      onSaved(row);
    } catch (e) { setErr(posErrorText(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    const linked = products.filter(p => p.supplierId === supplier.id).length;
    const poCount = pos.filter(p => p.supplier_id === supplier.id).length;
    const warn = (linked ? "\n· " + linked + zhEn(" 件貨品會變成「未指定供應商」", " items will become \"No supplier\"") : "") + (poCount ? "\n· " + poCount + zhEn(" 張採購單會保留，但唔再顯示供應商", " purchase orders are kept but no longer show the supplier") : "");
    if (!window.confirm(zhEn("刪除供應商「", "Delete supplier \"") + supplier.name + zhEn("」？", "\"?") + warn)) return;
    setBusy(true); setErr("");
    try { await deleteSupplier(supplier.id); onDeleted(supplier.id); }
    catch (e) { setErr(posErrorText(e)); setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} z={z} title={isNew ? zhEn("新增供應商", "New supplier") : zhEn("編輯供應商", "Edit supplier")}
      footer={<div style={{ display: "flex", gap: 8 }}>
        {!isNew && <Btn kind="danger" disabled={busy} onClick={del}><Trash2 size={17} /></Btn>}
        <Btn style={{ flex: 1 }} disabled={busy} onClick={save}>{busy ? zhEn("儲存中…", "Saving…") : zhEn("儲存", "Save")}</Btn>
      </div>}>
      <Field label={zhEn("名稱 *", "Name *")} value={f.name} onChange={set("name")} placeholder={zhEn("例如 寵物用品批發", "e.g. Pet supplies wholesale")} autoFocus={isNew} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label={zhEn("聯絡人", "Contact person")} value={f.contact} onChange={set("contact")} />
        <Field label={zhEn("電話", "Phone")} value={f.phone} onChange={set("phone")} inputMode="tel" />
      </div>
      <Field label={zhEn("電郵", "Email")} value={f.email} onChange={set("email")} inputMode="email" />
      <Field label={zhEn("地址", "Address")} value={f.address} onChange={set("address")} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <Field label={zhEn("到貨日數", "Lead time (days)")} value={f.lead_time_days} onChange={set("lead_time_days")} inputMode="numeric" />
        <Field label={zhEn("最低訂貨額", "Minimum order amount")} value={f.moq_amount} onChange={set("moq_amount")} inputMode="decimal" placeholder="0" />
        <Field label={zhEn("最低訂貨量", "Minimum order quantity")} value={f.moq_qty} onChange={set("moq_qty")} inputMode="decimal" placeholder="0" />
      </div>
      <Label>{zhEn("備註", "Note")}</Label>
      <textarea value={f.notes} onChange={set("notes")} rows={3} style={{ ...inputStyle, resize: "vertical", marginBottom: 8 }} placeholder={zhEn("例如 月結、送貨日", "e.g. monthly account, delivery days")} />
      {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, textAlign: "center" }}>{err}</p>}
    </Sheet>
  );
}

// ── PO editor ───────────────────────────────────────────────────────────────
const lineFromRaw = (l) => ({
  key: l.id, id: l.id, productId: l.product_id, name: l.name, sku: l.sku, qty: num(l.qty), freeQty: num(l.free_qty),
  cost: String(num(l.cost)), discount: num(l.discount) ? String(num(l.discount)) : "", expiryDate: l.expiry_date || "", received: num(l.received_qty),
});
const newLine = (p, qty = 1) => { const id = newId(); return { key: id, id, productId: p.id, name: p.name, sku: p.sku, qty, freeQty: 0, cost: String(p.cost || ""), discount: "", expiryDate: "", received: 0 }; };
const restockQty = (p) => Math.max(1, p.reorderQty || 0, (p.lowStock || 0) * 2 - p.stock);

function PoEditor({ tenant, sym, po, seed = {}, suppliers, supById, products, onScanOpen, onSupplierSaved, onClose, onSaved, onProductsLinked }) {
  const wide = useWide(900);
  const isNew = !po;
  const [id] = useState(() => po?.id || newId());
  const [supplierId, setSupplierId] = useState(po ? po.supplier_id || "" : seed.supplierId || "");
  const [orderDate, setOrderDate] = useState(po?.order_date || seed.orderDate || todayIso());
  const [expectedDate, setExpectedDate] = useState(po ? po.expected_date || "" : addDays(todayIso(), supById[seed.supplierId]?.lead_time_days ?? 7));
  const [expTouched, setExpTouched] = useState(!!po);
  const [supplierRef, setSupplierRef] = useState(po?.supplier_ref || seed.supplierRef || "");
  const [discount, setDiscount] = useState(po && num(po.discount) ? String(num(po.discount)) : seed.discount ? String(seed.discount) : "");
  const [discountType, setDiscountType] = useState(po?.discount_type || "amount");
  const [shippingFee, setShippingFee] = useState(po && num(po.shipping_fee) ? String(num(po.shipping_fee)) : seed.shippingFee ? String(seed.shippingFee) : "");
  const [taxRate, setTaxRate] = useState(po && num(po.tax_rate) ? String(num(po.tax_rate)) : "");
  const moneyLocked = po?.status === "partial";
  const [notes, setNotes] = useState(po?.notes || "");
  const [lines, setLines] = useState(() => po ? sortLines(po).map(lineFromRaw) : (seed.lines || []));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [newSup, setNewSup] = useState(false);
  const status = po?.status || "draft";

  const pickSupplier = (sid) => {
    setSupplierId(sid);
    if (!expTouched) setExpectedDate(addDays(orderDate, supById[sid]?.lead_time_days ?? 7));
  };
  const add = (p, by = 1) => setLines(ls => {
    const i = ls.findIndex(l => l.productId === p.id);
    if (i >= 0) return ls.map((l, j) => j === i ? { ...l, qty: round2(l.qty + by) } : l);
    return [...ls, newLine(p, by)];
  });
  const addMany = (list) => {
    if (!list.length) { setErr(zhEn("冇符合嘅貨品", "No matching items")); return; }
    setErr("");
    setLines(ls => {
      const have = new Set(ls.map(l => l.productId));
      return [...ls, ...list.filter(p => !have.has(p.id)).map(p => newLine(p, restockQty(p)))];
    });
  };
  const patch = (key, k, v) => setLines(ls => ls.map(l => l.key === key ? { ...l, [k]: v } : l));

  const found = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return products.filter(p => productMatches(p, s, supplierId)).slice(0, 8);
  }, [products, q, supplierId]);
  // A new supplier has no linked products yet — then show every product, so
  // 「低庫存」 quick-add isn't empty on day one.
  const supHasProducts = !!supplierId && products.some(p => p.supplierId === supplierId);
  const ofSupplier = (p) => !supHasProducts || p.supplierId === supplierId;
  const lowList = products.filter(p => p.trackStock && p.stock <= p.lowStock && ofSupplier(p));
  const supAll = supplierId ? products.filter(p => p.supplierId === supplierId) : [];

  const scan = () => onScanOpen((code) => {
    const hit = findByCode(products, code, supplierId);
    if (hit) { add(hit); return true; }
    return false;
  }, zhEn("連續掃描加入採購單（每掃一次 +1）", "Scan continuously to add to the order (+1 per scan)"), true);

  const t = poTotals({ lines, discount, discountType, shippingFee, taxRate });
  const units = lines.reduce((s, l) => s + l.qty + l.freeQty, 0);

  const save = async (to) => {
    const use = lines.filter(l => l.qty > 0 || l.freeQty > 0 || l.received > 0);
    if (!use.length) { setErr(zhEn("加入最少一件貨品", "Add at least one item")); return; }
    if (to === "pending" && !supplierId) { setErr(zhEn("落單前請揀供應商", "Choose a supplier before ordering")); return; }
    setBusy(true); setErr("");
    try {
      const row = await savePurchaseOrder({
        id, tenantId: tenant.id, supplierId: supplierId || null, status: to, orderDate, expectedDate: expectedDate || null,
        supplierRef: supplierRef.trim(), discount: Math.max(0, num(discount)), discountType, shippingFee: Math.max(0, num(shippingFee)),
        taxRate: Math.max(0, num(taxRate)), notes: notes.trim(),
        lines: use.map(l => ({ id: l.id, productId: l.productId || null, name: l.name, sku: l.sku || "", qty: l.qty, freeQty: l.freeQty, cost: Math.max(0, num(l.cost)), discount: Math.max(0, num(l.discount)), expiryDate: l.expiryDate || null })),
      });
      // AI lines carry the supplier's own item code — remember it on the
      // product so the next scan / search matches instantly.
      // Only lines the AI matched and the user left alone are learned.
      if (supplierId) {
        const learned = [];
        for (const l of use) {
          const prod = l.productId && l.supplierCode && !l.aiUnmatched && products.find(x => x.id === l.productId);
          if (prod && !(prod.supplierCodes || {})[supplierId]) {
            try { const codes = await setSupplierCode({ tenantId: tenant.id, productId: prod.id, supplierId, code: l.supplierCode }); learned.push({ id: prod.id, codes }); } catch { /* best effort */ }
          }
        }
        if (learned.length) onProductsLinked?.(learned.map(x => ({ id: x.id, supplierCodes: x.codes })));
      }
      // Products ordered from a supplier with none set get linked to it, so
      // 建議採購 / supplier filters build up without manual setup.
      if (supplierId) {
        const unlinked = [...new Set(use.map(l => l.productId).filter(pid => pid && !products.find(p => p.id === pid)?.supplierId))];
        if (unlinked.length) {
          try { const rows = await bulkPatchPosProducts(unlinked, { supplierId }); onProductsLinked?.(rows); } catch { /* the PO is saved; linking is a nicety */ }
        }
      }
      onSaved(row);
    } catch (e) { setErr(poErr(e)); } finally { setBusy(false); }
  };

  const sup = supById[supplierId];
  const summary = (
    <div>
      <Row l={zhEn("小計", "Subtotal")} v={money(t.subtotal, sym)} />
      {t.discount > 0 && <Row l={zhEn("折扣", "Discount") + (discountType === "percent" ? " " + num(discount) + "%" : "")} v={"−" + money(t.discount, sym)} />}
      {t.tax > 0 && <Row l={zhEn("稅 ", "Tax ") + num(taxRate) + "%"} v={money(t.tax, sym)} />}
      {t.shipping > 0 && <Row l={zhEn("運費", "Shipping")} v={money(t.shipping, sym)} />}
      <Row l={zhEn("合計 · ", "Total · ") + lines.length + zhEn(" 項 ", " lines ") + qtyTxt(units) + zhEn(" 件", " items")} v={money(t.total, sym)} big />
      {sup && num(sup.moq_amount) > t.subtotal && <p style={{ margin: "4px 0 0", fontSize: 12, fontWeight: 800, color: C.amber }}>{zhEn("未夠最低訂貨額", "Below the minimum order amount")} {money(sup.moq_amount, sym)}</p>}
      {sup && num(sup.moq_qty) > units && <p style={{ margin: "4px 0 0", fontSize: 12, fontWeight: 800, color: C.amber }}>{zhEn("未夠最低訂貨量", "Below the minimum order quantity")} {qtyTxt(sup.moq_qty)} {zhEn("件", "items")}</p>}
    </div>
  );

  return (
    <Sheet open onClose={onClose} maxWidth={wide ? 980 : 560} title={isNew ? (seed.aiNote ? zhEn("🤖 AI 採購單（請核對）", "🤖 AI purchase order (please check)") : zhEn("新增採購單", "New purchase order")) : zhEn("編輯 ", "Edit ") + poNo(po.number)}
      footer={<div>
        {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, textAlign: "center", margin: "0 0 8px" }}>{err}</p>}
        {!wide && <div style={{ marginBottom: 10 }}>{summary}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          {status === "draft" && <Btn kind="ghost" style={{ flex: 1 }} disabled={busy} onClick={() => save("draft")}>{zhEn("儲存草稿", "Save draft")}</Btn>}
          {status === "draft"
            ? <Btn style={{ flex: 1.4 }} disabled={busy} onClick={() => save("pending")}><Send size={17} /> {busy ? zhEn("處理中…", "Processing…") : zhEn("落單", "Ordered")}</Btn>
            : <Btn style={{ flex: 1 }} disabled={busy} onClick={() => save(status)}>{busy ? zhEn("儲存中…", "Saving…") : zhEn("儲存修改", "Save changes")}</Btn>}
        </div>
      </div>}>
      <div style={{ display: wide ? "grid" : "block", gridTemplateColumns: "minmax(0,1fr) 320px", gap: 18 }}>
        <div>
          {seed.aiNote && (
            <div style={{ background: "linear-gradient(135deg,#F5F3FF,#FFF1F5)", border: "1px solid #E9D5FF", borderRadius: 14, padding: "10px 12px", marginBottom: 12, fontSize: 13, fontWeight: 800, color: "#5B21B6", lineHeight: 1.5 }}>
              {zhEn("🤖 AI 讀到", "🤖 AI read")} {seed.aiNote.total} {zhEn("項", "lines")}{seed.aiNote.unmatched ? zhEn("，其中 ", ", of which ") + seed.aiNote.unmatched + zhEn(" 項未配對（下面黃色）", " lines are unmatched (yellow below)") : zhEn("，全部已配對", ", all matched")}{zhEn("。", ". ")}
              {!supplierId && seed.aiNote.supplierName ? zhEn(" 發票供應商：「", " Invoice supplier: \"") + seed.aiNote.supplierName + zhEn("」— 請揀或者新增。", "\" — please choose or add it.") : ""}
              {seed.aiNote.currency && seed.aiNote.currency !== "HKD" ? zhEn(" ⚠️ 發票貨幣係 ", " ⚠️ The invoice currency is ") + seed.aiNote.currency + zhEn("，請將成本改做港幣。", ", please change the costs to HKD.") : ""}
              {zhEn("核對好先落單。", "Check it before ordering.")}
            </div>
          )}
          <Label>{zhEn("供應商", "Suppliers")}</Label>
          <div className="pos-noscroll" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 12, paddingBottom: 2 }}>
            <Chip on={!supplierId} onClick={() => pickSupplier("")}>{zhEn("未指定", "None")}</Chip>
            {suppliers.map(s => <Chip key={s.id} on={supplierId === s.id} onClick={() => pickSupplier(s.id)}>{s.name}</Chip>)}
            <Chip onClick={() => setNewSup(true)} style={{ background: C.accentSoft, color: C.accent }}><Plus size={13} style={{ verticalAlign: -2 }} /> {zhEn("新供應商", "New supplier")}</Chip>
          </div>

          <Label>{zhEn("貨品", "Items")}</Label>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1, position: "relative" }}>
              <Search size={18} color={C.faint} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder={zhEn("名稱 / SKU / 條碼 / 供應商編號", "Name / SKU / barcode / supplier code")} style={{ ...inputStyle, paddingLeft: 38, borderRadius: 14 }}
                onKeyDown={e => { if (e.key === "Enter") { const hit = findByCode(products, q, supplierId); if (hit) { add(hit); setQ(""); } } }} />
            </div>
            <Btn onClick={scan} style={{ padding: "0 14px" }}><ScanLine size={20} /> {zhEn("掃描", "Scan")}</Btn>
          </div>
          {found.map(p => (
            <button key={p.id} type="button" onClick={() => { add(p); setQ(""); }} style={pickRow}>
              <ProductThumb p={p} size={34} radius={10} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 800, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.sub }}>{[p.sku, supplierId && p.supplierCodes?.[supplierId]].filter(Boolean).join(" · ") || " "}</span>
              </span>
              <span style={{ fontWeight: 800, color: C.sub, fontSize: 13 }}>{p.trackStock ? zhEn("存 ", "Stock ") + p.stock : ""}</span>
            </button>
          ))}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "8px 0 12px" }}>
            <Btn kind="soft" small onClick={() => addMany(lowList)}>{zhEn("＋ 低庫存 / 冇貨", "+ Low stock / out of stock")}{lowList.length ? " (" + lowList.length + ")" : ""}</Btn>
            {supplierId && <Btn kind="soft" small onClick={() => addMany(supAll)}>{zhEn("＋ 此供應商全部貨品 (", "+ All items from this supplier (")}{supAll.length})</Btn>}
          </div>

          {lines.length === 0 ? <Empty icon="📋" title={zhEn("未有貨品", "No items yet")} sub={zhEn("搜尋、掃描，或者一撳加入低庫存貨品", "Search, scan, or add low-stock items in one tap")} />
            : lines.map(l => {
              const p = products.find(x => x.id === l.productId);
              const code = supplierId && p?.supplierCodes?.[supplierId];
              const lt = Math.max(0, num(l.cost) * l.qty - num(l.discount));
              return (
                <Card key={l.key} style={{ padding: 10, marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <ProductThumb p={p || { category: "" }} size={40} radius={10} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 900, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</p>
                      {!l.productId && (
                        <select value="" onChange={e => { const m = products.find(x => x.id === e.target.value); if (m) setLines(ls => ls.map(x => x.key === l.key ? { ...x, productId: m.id, name: m.name, sku: m.sku, aiUnmatched: false } : x)); }}
                          style={{ ...inputStyle, padding: "6px 8px", fontSize: 12.5, margin: "4px 0", borderColor: C.amber, background: C.amberSoft }}>
                          <option value="">{zhEn("⚠️ 未配對商品（收貨唔會入庫存）— 揀對應商品", "⚠️ Unmatched product (receiving won't add to stock) — choose the matching product")}</option>
                          {products.slice().sort((a, b) => a.name.localeCompare(b.name)).map(x => <option key={x.id} value={x.id}>{x.name}{x.sku ? " · " + x.sku : ""}</option>)}
                        </select>
                      )}
                      <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.sub }}>
                        {[code && zhEn("供應商編號 ", "Supplier code ") + code, p?.trackStock && zhEn("存 ", "Stock ") + p.stock, l.received > 0 && zhEn("已收 ", "Received ") + qtyTxt(l.received)].filter(Boolean).join(" · ") || l.sku || " "}
                      </p>
                    </div>
                    <span style={{ fontWeight: 900, color: C.ink }}>{money(lt, sym)}</span>
                    <button type="button" disabled={l.received > 0} title={l.received > 0 ? zhEn("已收貨，唔可以刪除", "Already received — can't be deleted") : zhEn("刪除", "Delete")} onClick={() => setLines(ls => ls.filter(x => x.key !== l.key))}
                      style={{ border: "none", background: "none", color: l.received > 0 ? C.line : C.faint, cursor: l.received > 0 ? "default" : "pointer", padding: 4 }}>
                      {l.received > 0 ? <Lock size={17} /> : <Trash2 size={18} />}
                    </button>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <span style={miniLbl}>{zhEn("數量", "Qty")}</span>
                    <Stepper value={l.qty} min={Math.max(0, l.received - l.freeQty)} onChange={v => patch(l.key, "qty", v)} />
                    <span style={{ flex: 1 }} />
                    <span style={miniLbl}>{zhEn("贈品", "Free item")}</span>
                    <Stepper value={l.freeQty} min={Math.max(0, l.received - l.qty)} onChange={v => patch(l.key, "freeQty", v)} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                    <label><span style={miniLbl}>{zhEn("單位成本", "Unit cost")}</span>
                      <input inputMode="decimal" value={l.cost} disabled={moneyLocked && l.received > 0} onChange={e => patch(l.key, "cost", e.target.value)} style={{ ...inputStyle, padding: "8px 10px", marginTop: 3 }} /></label>
                    <label><span style={miniLbl}>{zhEn("折扣 $", "Discount $")}</span>
                      <input inputMode="decimal" value={l.discount} placeholder="0" disabled={moneyLocked && l.received > 0} onChange={e => patch(l.key, "discount", e.target.value)} style={{ ...inputStyle, padding: "8px 10px", marginTop: 3 }} /></label>
                  </div>
                </Card>
              );
            })}
        </div>

        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: wide ? 0 : 6 }}>
            <Field label={zhEn("落單日期", "Order date")} type="date" value={orderDate} onChange={e => { setOrderDate(e.target.value); if (!expTouched) setExpectedDate(addDays(e.target.value, sup?.lead_time_days ?? 7)); }} />
            <Field label={zhEn("預計到貨", "Expected delivery")} type="date" value={expectedDate} onChange={e => { setExpectedDate(e.target.value); setExpTouched(true); }} />
          </div>
          <Field label={zhEn("供應商單號 / 發票號", "Supplier reference / invoice no.")} value={supplierRef} onChange={e => setSupplierRef(e.target.value)} placeholder={zhEn("可留空", "optional")} />
          <Label>{zhEn("整單折扣", "Order discount")}</Label>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input inputMode="decimal" value={discount} placeholder="0" disabled={moneyLocked} onChange={e => setDiscount(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
            {!moneyLocked && <Seg value={discountType} onChange={setDiscountType} style={{ width: 110 }} options={[["amount", "$"], ["percent", "%"]]} />}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label={zhEn("運費", "Shipping")} value={shippingFee} disabled={moneyLocked} onChange={e => setShippingFee(e.target.value)} inputMode="decimal" placeholder="0" />
            <Field label={zhEn("稅率 %", "Tax rate %")} value={taxRate} disabled={moneyLocked} onChange={e => setTaxRate(e.target.value)} inputMode="decimal" placeholder="0" />
          </div>
          {moneyLocked && <p style={{ fontSize: 12, fontWeight: 700, color: C.sub, margin: "-4px 2px 12px" }}>{zhEn("已經有貨到咗：折扣、運費、稅同已到貨項目嘅成本鎖定（入貨成本已按呢啲計）。", "Stock has already arrived: the discount, shipping, tax and received lines' cost are locked (the stock cost was calculated from them).")}</p>}
          <Label>{zhEn("備註", "Note")}</Label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: "vertical", marginBottom: 12 }} placeholder={zhEn("例如 送貨時間、包裝要求", "e.g. delivery time, packaging requirements")} />
          {wide && <Card style={{ padding: 14 }}>{summary}</Card>}
        </div>
      </div>

      {newSup && <SupplierSheet tenant={tenant} supplier={{}} products={products} pos={[]} z={500} onClose={() => setNewSup(false)}
        onSaved={(row) => { onSupplierSaved(row); setNewSup(false); setSupplierId(row.id); if (!expTouched) setExpectedDate(addDays(orderDate, row.lead_time_days ?? 7)); }}
        onDeleted={() => setNewSup(false)} />}
    </Sheet>
  );
}
const miniLbl = { fontSize: 12, fontWeight: 900, color: C.sub };
const pickRow = { width: "100%", display: "flex", alignItems: "center", gap: 10, padding: 8, border: "none", borderBottom: "1px solid " + C.line, background: "#fff", fontFamily: "inherit", cursor: "pointer", textAlign: "left" };
const Row = ({ l, v, big }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: big ? "6px 0 0" : "2px 0", borderTop: big ? "1px solid " + C.line : "none", marginTop: big ? 4 : 0 }}>
    <span style={{ fontSize: big ? 14 : 13, fontWeight: big ? 900 : 800, color: big ? C.ink : C.sub }}>{l}</span>
    <span style={{ fontSize: big ? 20 : 14, fontWeight: 900, color: C.ink }}>{v}</span>
  </div>
);

// ── PO detail ───────────────────────────────────────────────────────────────
function PoDetail({ po, supplier, products, tenant, sym, flash, onScanOpen, onClose, onEdit, onChanged }) {
  const wide = useWide(900);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [receiving, setReceiving] = useState(false);
  const [returning, setReturning] = useState(null);
  const lines = sortLines(po);
  const t = rawTotals(po);
  const got = poGot(po), total = poQty(po);
  const st = PO_STATUS[po.status] || [po.status, "grey"];
  const late = isOverdue(po, todayIso());
  const byId = useMemo(() => Object.fromEntries(products.map(p => [p.id, p])), [products]);

  const setStatus = async (status, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true); setErr("");
    try { await setPurchaseOrderStatus({ poId: po.id, status }); await onChanged(false); flash(poNo(po.number) + " · " + (PO_STATUS[status]?.[0] || status)); }
    catch (e) { setErr(poErr(e)); } finally { setBusy(false); }
  };
  const waText = () => {
    const out = [`${tenant?.name || ""} 採購單 ${poNo(po.number)}`, `落單日期：${po.order_date || ""}`];
    if (po.expected_date) out.push(`希望到貨：${po.expected_date}`);
    out.push("");
    lines.forEach(l => {
      const code = supplier && byId[l.product_id]?.supplierCodes?.[supplier.id];
      out.push(`• ${l.name}${code ? zhEn(`（${code}）`, ` (${code})`) : l.sku ? zhEn(`（${l.sku}）`, ` (${l.sku})`) : ""} × ${qtyTxt(l.qty)}${num(l.free_qty) ? ` + 贈 ${qtyTxt(l.free_qty)}` : ""}`);
    });
    out.push("", `合計：${money(po.total, sym)}`);
    if (po.notes) out.push(`備註：${po.notes}`);
    out.push("", "唔該確認，多謝！");
    const d = waDigits(supplier?.phone);
    window.open((d ? "https://wa.me/" + d : "https://wa.me/") + "?text=" + encodeURIComponent(out.join("\n")), "_blank");
  };

  const can = {
    edit: OPEN.includes(po.status),
    order: po.status === "draft",
    receive: po.status === "pending" || po.status === "partial",
    close: po.status === "pending" || po.status === "partial",
    cancel: (po.status === "draft" || po.status === "pending") && got === 0,
  };

  return (
    <Sheet open onClose={onClose} maxWidth={760} title={<span>{poNo(po.number)} <Pill tone={st[1]} style={{ verticalAlign: 3 }}>{st[0]}</Pill>{late && <Pill tone="red" style={{ verticalAlign: 3, marginLeft: 4 }}>{zhEn("逾期", "Overdue")}</Pill>}</span>}
      footer={<div>
        {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, textAlign: "center", margin: "0 0 8px" }}>{err}</p>}
        <div style={{ display: "flex", gap: 8 }}>
          {can.edit && <Btn kind="ghost" disabled={busy} onClick={onEdit}><Pencil size={16} /> {zhEn("編輯", "Edit ")}</Btn>}
          {can.order && <Btn style={{ flex: 1 }} disabled={busy || !lines.length} onClick={() => setStatus("pending")}><Send size={17} /> {zhEn("落單", "Ordered")}</Btn>}
          {can.receive && <Btn kind="green" style={{ flex: 1 }} disabled={busy} onClick={() => setReceiving(true)}><PackageCheck size={18} /> {zhEn("收貨", "Receive")}</Btn>}
          {!can.order && !can.receive && <Btn kind="ghost" style={{ flex: 1 }} onClick={() => printPO(po, supplier, byId, tenant, sym)}><Printer size={16} /> {zhEn("列印", "Print")}</Btn>}
        </div>
      </div>}>
      <div style={{ display: "grid", gridTemplateColumns: wide ? "1fr 1fr" : "1fr", gap: 8, marginBottom: 12 }}>
        <Card style={{ padding: 12 }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 900, color: C.ink }}>{supplier?.name || zhEn("未指定供應商", "No supplier")}</p>
          {supplier && <p style={{ margin: "2px 0 0", fontSize: 12.5, fontWeight: 700, color: C.sub }}>{[supplier.contact, supplier.phone, supplier.email].filter(Boolean).join(" · ") || "—"}</p>}
          {supplier?.phone && (
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <a href={"tel:" + supplier.phone.replace(/[^\d+]/g, "")} style={linkBtn}><Phone size={15} /> {zhEn("致電", "Call")}</a>
              {supplier.email && <a href={"mailto:" + supplier.email + "?subject=" + encodeURIComponent(poNo(po.number))} style={linkBtn}><Mail size={15} /> {zhEn("電郵", "Email")}</a>}
            </div>
          )}
        </Card>
        <Card style={{ padding: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 13, fontWeight: 800 }}>
          <Info k={zhEn("落單", "Ordered")} v={fmtDay(po.order_date)} />
          <Info k={zhEn("預計到貨", "Expected delivery")} v={fmtDay(po.expected_date)} tone={late ? C.red : undefined} />
          <Info k={zhEn("已收", "Received")} v={qtyTxt(got) + " / " + qtyTxt(total) + zhEn(" 件", " items")} tone={got >= total && total > 0 ? C.green : undefined} />
          <Info k={zhEn("供應商單號", "Supplier reference")} v={po.supplier_ref || "—"} />
        </Card>
      </div>

      <Label>{zhEn("貨品", "Items")}</Label>
      {lines.map(l => {
        const p = byId[l.product_id];
        const ordered = num(l.qty) + num(l.free_qty), rec = num(l.received_qty), ret = num(l.returned_qty);
        const lt = Math.max(0, num(l.cost) * num(l.qty) - num(l.discount));
        return (
          <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 2px", borderBottom: "1px solid " + C.line }}>
            <ProductThumb p={p || { category: "" }} size={38} radius={10} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 900, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</p>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.sub }}>
                {qtyTxt(l.qty)} × {money(l.cost, sym)}{num(l.discount) ? " − " + money(l.discount, sym) : ""}{num(l.free_qty) ? zhEn(" · 贈 ", " · free ") + qtyTxt(l.free_qty) : ""}
                {l.expiry_date ? zhEn(" · 到期 ", " · expires ") + fmtDay(l.expiry_date) : ""}
              </p>
              {po.status !== "draft" && (
                <p style={{ margin: "2px 0 0", fontSize: 12, fontWeight: 800 }}>
                  <span style={{ color: rec >= ordered ? C.green : rec > 0 ? C.amber : C.faint }}>{zhEn("已收", "Received")} {qtyTxt(rec)}/{qtyTxt(ordered)}</span>
                  {ret > 0 && <span style={{ color: C.red }}> {zhEn("· 已退", "· refunded")} {qtyTxt(ret)}</span>}
                </p>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ margin: 0, fontWeight: 900, color: C.ink }}>{money(lt, sym)}</p>
              {rec > ret && po.status !== "cancelled" && (
                <button type="button" onClick={() => setReturning(l)} style={{ border: "none", background: "none", color: C.red, fontWeight: 900, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", padding: "2px 0" }}>
                  <Undo2 size={13} style={{ verticalAlign: -2 }} /> {zhEn("退貨", "Return")}
                </button>
              )}
            </div>
          </div>
        );
      })}

      <div style={{ margin: "12px 0" }}>
        <Row l={zhEn("小計", "Subtotal")} v={money(po.subtotal ?? t.subtotal, sym)} />
        {t.discount > 0 && <Row l={zhEn("折扣", "Discount") + (po.discount_type === "percent" ? " " + num(po.discount) + "%" : "")} v={"−" + money(t.discount, sym)} />}
        {t.tax > 0 && <Row l={zhEn("稅 ", "Tax ") + num(po.tax_rate) + "%"} v={money(t.tax, sym)} />}
        {t.shipping > 0 && <Row l={zhEn("運費", "Shipping")} v={money(t.shipping, sym)} />}
        <Row l={zhEn("合計", "Total")} v={money(po.total, sym)} big />
      </div>
      {po.notes && <p style={{ fontSize: 13, fontWeight: 700, color: C.sub, background: "#fff", border: "1px solid " + C.line, borderRadius: 14, padding: 10 }}>{zhEn("備註：", "Note:")}{po.notes}</p>}
      <ReceiptLog lines={lines} />

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <Btn kind="ghost" small onClick={() => printPO(po, supplier, byId, tenant, sym)}><Printer size={15} /> {zhEn("列印 / PDF", "Print / PDF")}</Btn>
        <Btn kind="ghost" small onClick={waText} style={{ color: "#128C7E" }}><MessageCircle size={15} /> {zhEn("WhatsApp 供應商", "WhatsApp supplier")}</Btn>
        {can.close && <Btn kind="ghost" small disabled={busy} onClick={() => setStatus("closed", zhEn("關閉 ", "Close ") + poNo(po.number) + zhEn("？未到嘅貨唔會再收。", "? Items that haven't arrived won't be received."))}><Lock size={15} /> {zhEn("關閉", "Close")}</Btn>}
        {can.cancel && <Btn kind="danger" small disabled={busy} onClick={() => setStatus("cancelled", zhEn("取消 ", "Cancel ") + poNo(po.number) + zhEn("？", "?"))}><XCircle size={15} /> {zhEn("取消採購單", "Cancel purchase order")}</Btn>}
      </div>

      {receiving && <ReceiveSheet po={po} lines={lines} byId={byId} supplier={supplier} onScanOpen={onScanOpen} onClose={() => setReceiving(false)}
        onDone={async (n) => { setReceiving(false); flash(zhEn("已收貨 ", "Received ") + qtyTxt(n) + zhEn(" 件，庫存已更新", " items, stock updated")); await onChanged(true); }} />}
      {returning && <ReturnSheet po={po} line={returning} sym={sym} onClose={() => setReturning(null)}
        onDone={async (n) => { setReturning(null); flash(zhEn("已退貨 ", "Returned ") + qtyTxt(n) + zhEn(" 件", " items")); await onChanged(true); }} />}
    </Sheet>
  );
}
const Info = ({ k, v, tone }) => (
  <div><p style={{ margin: 0, fontSize: 11.5, fontWeight: 900, color: C.sub }}>{k}</p><p style={{ margin: 0, fontWeight: 900, color: tone || C.ink }}>{v}</p></div>
);

function ReceiptLog({ lines }) {
  const ev = [];
  lines.forEach(l => {
    (l.meta?.receipts || []).forEach(r => ev.push({ at: r.at, text: zhEn("收 ", "Received ") + qtyTxt(r.qty) + " × " + l.name, tone: C.green }));
    (l.meta?.returns || []).forEach(r => ev.push({ at: r.at, text: zhEn("退 ", "Returned ") + qtyTxt(r.qty) + " × " + l.name + (r.reason ? zhEn("（", " (") + r.reason + zhEn("）", ")") : ""), tone: C.red }));
  });
  if (!ev.length) return null;
  ev.sort((a, b) => String(b.at).localeCompare(String(a.at)));
  return (
    <div style={{ marginTop: 12 }}>
      <Label>{zhEn("收貨 / 退貨記錄", "Receiving / returns log")}</Label>
      {ev.slice(0, 30).map((e, i) => (
        <p key={i} style={{ margin: 0, padding: "5px 2px", borderBottom: "1px solid " + C.line, fontSize: 12.5, fontWeight: 800, color: C.sub }}>
          <span style={{ color: e.tone }}>●</span> {e.at ? new Date(e.at).toLocaleString(dateLocale(), { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""} · {e.text}
        </p>
      ))}
    </div>
  );
}

// ── 收貨 ────────────────────────────────────────────────────────────────────
function ReceiveSheet({ po, lines, byId, supplier, onScanOpen, onClose, onDone }) {
  const [receiptId] = useState(() => newId());
  const open = lines.filter(l => num(l.qty) + num(l.free_qty) - num(l.received_qty) > 0);
  const remain = (l) => round2(num(l.qty) + num(l.free_qty) - num(l.received_qty));
  const [qty, setQtyState] = useState(() => Object.fromEntries(open.map(l => [l.id, remain(l)])));
  const qtyRef = useRef(qty);
  const setQty = (v) => { const n = typeof v === "function" ? v(qtyRef.current) : v; qtyRef.current = n; setQtyState(n); };
  const [exp, setExp] = useState(() => Object.fromEntries(open.map(l => [l.id, l.expiry_date || ""])));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const all = open.every(l => qty[l.id] === remain(l));
  const count = open.reduce((s, l) => s + (qty[l.id] || 0), 0);

  const scan = () => {
    setQty(Object.fromEntries(open.map(l => [l.id, 0])));
    onScanOpen((code) => {
      const c = String(code).trim();
      const line = open.find(l => { const p = byId[l.product_id]; return (p && (p.barcode === c || p.sku === c || (supplier && p.supplierCodes?.[supplier.id] === c))) || (l.sku && l.sku === c); });
      if (!line) return false;
      const n = (qtyRef.current[line.id] || 0) + 1;
      if (n > remain(line)) return false;
      setQty({ ...qtyRef.current, [line.id]: n });
      return true;
    }, zhEn("連續掃描收貨（每掃一次 +1）", "Scan continuously to receive (+1 per scan)"), true);
  };
  const submit = async () => {
    const items = open.filter(l => (qty[l.id] || 0) > 0).map(l => ({ lineId: l.id, qty: qty[l.id], expiryDate: exp[l.id] || null }));
    if (!items.length) { setErr(zhEn("未揀收貨數量", "No received quantities chosen")); return; }
    setBusy(true); setErr("");
    // Same receiptId on a retry → the server ignores the repeat (lost reply on a weak connection).
    try { await receivePurchaseOrder({ poId: po.id, receiptId, note: note.trim(), lines: items }); await onDone(count); }
    catch (e) { setErr(poErr(e)); setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} z={450} title={zhEn("收貨 · ", "Receive · ") + poNo(po.number)}
      footer={<div>
        {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, textAlign: "center", margin: "0 0 8px" }}>{err}</p>}
        <Btn kind="green" style={{ width: "100%", padding: 16 }} disabled={busy || count <= 0} onClick={submit}>{busy ? zhEn("處理中…", "Processing…") : zhEn("確認收貨 · ", "Confirm receipt · ") + qtyTxt(count) + zhEn(" 件", " items")}</Btn>
      </div>}>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <Btn kind={all ? "soft" : "ghost"} small onClick={() => setQty(all ? Object.fromEntries(open.map(l => [l.id, 0])) : Object.fromEntries(open.map(l => [l.id, remain(l)])))}>
          {all ? <CheckSquare size={15} /> : <Square size={15} />} {zhEn("全部收齊", "Receive all")}
        </Btn>
        <Btn kind="ghost" small onClick={() => setQty(Object.fromEntries(open.map(l => [l.id, 0])))}><RotateCcw size={15} /> {zhEn("清零", "Reset")}</Btn>
        <span style={{ flex: 1 }} />
        <Btn small onClick={scan}><ScanLine size={16} /> {zhEn("逐件掃描", "Scan items")}</Btn>
      </div>
      <p style={{ fontSize: 12.5, fontWeight: 700, color: C.sub, margin: "0 2px 10px", lineHeight: 1.5 }}>{zhEn("到幾多收幾多 — 未到嘅可以下次再收。成本會連運費、折扣、稅按比例攤分，再加權平均入貨品成本。", "Receive what has arrived — the rest can be received next time. Shipping, discount and tax are spread across the lines, then averaged into the item cost.")}</p>
      {open.length === 0 ? <Empty icon="✅" title={zhEn("全部已收齊", "Everything has been received")} /> : open.map(l => {
        const p = byId[l.product_id];
        return (
          <Card key={l.id} style={{ padding: 10, marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ProductThumb p={p || { category: "" }} size={40} radius={10} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 900, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</p>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.sub }}>{zhEn("尚欠", "Still owed")} {qtyTxt(remain(l))}{num(l.received_qty) ? zhEn(" · 已收 ", " · received ") + qtyTxt(l.received_qty) : ""}{p?.trackStock ? zhEn(" · 存 ", " · stock ") + p.stock : ""}</p>
              </div>
              <Stepper value={qty[l.id] || 0} min={0} max={remain(l)} onChange={v => setQty(q => ({ ...q, [l.id]: v }))} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <span style={{ ...miniLbl, flex: 1 }}>{zhEn("到期日（可留空）", "Expiry date (optional)")}</span>
              <input type="date" value={exp[l.id] || ""} onChange={e => setExp(x => ({ ...x, [l.id]: e.target.value }))} style={{ ...inputStyle, width: 170, padding: "7px 10px", fontSize: 14 }} />
            </div>
          </Card>
        );
      })}
      <Field label={zhEn("備註", "Note")} value={note} onChange={e => setNote(e.target.value)} placeholder={zhEn("例如 送貨單號、收貨人", "e.g. delivery note no., receiver")} />
    </Sheet>
  );
}

// ── 退貨 ────────────────────────────────────────────────────────────────────
function ReturnSheet({ po, line, sym, onClose, onDone }) {
  const [returnId] = useState(() => newId());
  const max = round2(num(line.received_qty) - num(line.returned_qty));
  const [qty, setQty] = useState(Math.min(1, max) || max);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [deduct, setDeduct] = useState(true);
  const [refund, setRefund] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!reason) { setErr(zhEn("請揀退貨原因", "Choose a return reason")); return; }
    setBusy(true); setErr("");
    try {
      await returnPurchaseOrderLine({ poId: po.id, lineId: line.id, returnId, qty, reason: reason + (note.trim() ? " · " + note.trim() : ""), deductStock: deduct, refundAmount: refund === "" ? null : Math.max(0, num(refund)) });
      await onDone(qty);
    } catch (e) { setErr(poErr(e)); setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} z={450} title={zhEn("退貨 · ", "Return · ") + line.name}
      footer={<div>
        {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, textAlign: "center", margin: "0 0 8px" }}>{err}</p>}
        <Btn kind="danger" style={{ width: "100%", padding: 16 }} disabled={busy || qty <= 0} onClick={submit}><Undo2 size={17} /> {busy ? zhEn("處理中…", "Processing…") : zhEn("確認退貨 ", "Confirm return ") + qtyTxt(qty) + zhEn(" 件", " items")}</Btn>
      </div>}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span style={{ flex: 1, fontWeight: 900, color: C.ink }}>{zhEn("退貨數量", "Return quantity")} <span style={{ color: C.sub, fontWeight: 700, fontSize: 12.5 }}>{zhEn("（最多", " (max")} {qtyTxt(max)}{zhEn("）", ")")}</span></span>
        <Stepper value={qty} min={0} max={max} onChange={setQty} />
      </div>
      <Label>{zhEn("原因", "Reason")}</Label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {REASONS.map(r => <Chip key={r} on={reason === r} onClick={() => setReason(r)}>{r}</Chip>)}
      </div>
      <Field label={zhEn("備註", "Note")} value={note} onChange={e => setNote(e.target.value)} placeholder={zhEn("可留空", "optional")} />
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 2px", borderBottom: "1px solid " + C.line, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 900, color: C.ink }}>{zhEn("扣返庫存", "Take back from stock")}</p>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.sub }}>{zhEn("貨已經入咗倉先要扣；如果收貨時已經冇入倉就熄咗佢", "Only needed if the goods were put into stock; switch it off if they weren't stocked when received")}</p>
        </div>
        <Toggle on={deduct} onChange={setDeduct} />
      </div>
      <Field label={zhEn("供應商退款（", "Supplier refund (") + sym + zhEn("，可留空）", ", optional)")} value={refund} onChange={e => setRefund(e.target.value)} inputMode="decimal" placeholder={money(num(line.cost) * qty, sym)} hint={zhEn("只作記錄，唔會影響收銀", "For the record only — it doesn't affect the till")} />
    </Sheet>
  );
}

// ── 建議採購 ────────────────────────────────────────────────────────────────
function Suggest({ tenant, sym, products, pos, supById, wide, flash, onCreated, onOpenPo }) {
  const [sold, setSold] = useState(null);
  const [err, setErr] = useState("");
  const [pick, setPick] = useState({});     // productId → {on, qty}
  const [busy, setBusy] = useState("");
  const [made, setMade] = useState({});     // groupKey → po id
  const draftIds = useRef({});

  const loadSales = useCallback(async () => {
    setErr("");
    try {
      // Summed in SQL (PostgREST caps rows at ~1000, which undercounted busy shops).
      setSold(await fetchSalesVelocity(tenant.id, 30));
    } catch (e) { setErr(posErrorText(e)); setSold({}); }
  }, [tenant.id]);
  useEffect(() => { loadSales(); }, [loadSales]);

  const onOrder = useMemo(() => {
    const s = new Set();
    for (const p of pos) if (OPEN.includes(p.status)) for (const l of p.purchase_order_lines || [])
      if (l.product_id && num(l.qty) + num(l.free_qty) - num(l.received_qty) > 0) s.add(l.product_id);
    return s;
  }, [pos]);

  const groups = useMemo(() => {
    if (!sold) return [];
    const g = {};
    for (const p of products) {
      if (!p.trackStock || p.status === "draft" || onOrder.has(p.id)) continue;
      const sup = supById[p.supplierId];
      const lead = sup ? num(sup.lead_time_days ?? 7) : 7;
      const v = Math.max(0, (sold[p.id] || 0) / 30);
      const daysLeft = v > 0 ? Math.max(0, p.stock) / v : (p.stock <= 0 ? 0 : Infinity);
      if (!(p.stock <= p.lowStock || (v > 0 && daysLeft <= lead + 3))) continue;
      // Enough for 30 days + the lead time, minus what's already on the shelf.
      const qty = Math.max(Math.ceil(v * (30 + lead)) - Math.max(0, p.stock), p.reorderQty || 0, (p.lowStock || 0) * 2 - p.stock, 1);
      const key = sup ? sup.id : "";
      (g[key] = g[key] || { key, sup, lead, items: [] }).items.push({ p, v, daysLeft, qty, sold: sold[p.id] || 0 });
    }
    return Object.values(g).map(gr => {
      gr.items.sort((a, b) => byDays(a.daysLeft, b.daysLeft));
      const amt = gr.items.reduce((s, it) => s + it.qty * it.p.cost, 0);
      const units = gr.items.reduce((s, it) => s + it.qty, 0);
      let f = 1;
      if (gr.sup) {
        if (num(gr.sup.moq_amount) > 0 && amt > 0 && amt < num(gr.sup.moq_amount)) f = Math.max(f, num(gr.sup.moq_amount) / amt);
        if (num(gr.sup.moq_qty) > 0 && units < num(gr.sup.moq_qty)) f = Math.max(f, num(gr.sup.moq_qty) / units);
      }
      f = Math.min(3, f);
      if (f > 1) gr.items.forEach(it => { it.qty = Math.ceil(it.qty * f); });
      gr.scaled = f > 1;
      return gr;
    }).sort((a, b) => (a.key ? 0 : 1) - (b.key ? 0 : 1) || byDays(a.items[0]?.daysLeft ?? 0, b.items[0]?.daysLeft ?? 0));
  }, [products, sold, onOrder, supById]);

  const get = (it) => ({ on: true, qty: it.qty, ...pick[it.p.id] });
  const setItem = (id, v) => setPick(x => ({ ...x, [id]: { ...x[id], ...v } }));

  const create = async (gr) => {
    const chosen = gr.items.filter(it => get(it).on && get(it).qty > 0);
    if (!chosen.length) return;
    setBusy(gr.key || "_");
    try {
      const today = todayIso();
      const row = await savePurchaseOrder({
        // One id per group for this screen: a retry after a timeout updates the same draft.
        id: (draftIds.current[gr.key || "_"] = draftIds.current[gr.key || "_"] || newId()), tenantId: tenant.id, supplierId: gr.sup?.id || null, status: "draft", orderDate: today, expectedDate: addDays(today, gr.lead),
        supplierRef: "", discount: 0, discountType: "amount", shippingFee: 0, taxRate: 0, notes: "由建議採購建立",
        lines: chosen.map(it => ({ id: newId(), productId: it.p.id, name: it.p.name, sku: it.p.sku, qty: get(it).qty, freeQty: 0, cost: it.p.cost || 0, discount: 0, expiryDate: null })),
      });
      setMade(m => ({ ...m, [gr.key || "_"]: row }));
      flash(zhEn("已建立草稿 ", "Draft created ") + poNo(row.number));
      await onCreated();
    } catch (e) { flash(poErr(e), "error"); } finally { setBusy(""); }
  };

  if (sold === null) return <p style={{ textAlign: "center", color: C.sub, fontWeight: 800, padding: 40 }}>{zhEn("計算緊銷量…", "Working out sales…")}</p>;
  const madeList = Object.values(made);
  return (
    <div>
      <p style={{ fontSize: 12.5, fontWeight: 700, color: C.sub, margin: "0 2px 10px", lineHeight: 1.5 }}>
        <Lightbulb size={13} style={{ verticalAlign: -2 }} /> {zhEn("按過去 30 日銷量：低過警戒線，或者剩貨撐唔過「到貨日數 + 3 日」就建議補貨，數量夠賣 30 日 + 到貨期。已喺未完成採購單嘅貨品唔會重複建議。", "Based on the last 30 days of sales: suggests reordering when stock is below the alert level or won't last the \"lead time + 3 days\", in a quantity covering 30 days + lead time. Items already on an open purchase order aren't suggested again.")}
        <button type="button" onClick={() => { setSold(null); setPick({}); loadSales(); }} style={{ border: "none", background: "none", color: C.accent, fontWeight: 900, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5 }}>{zhEn("重新計算", "Recalculate")}</button>
      </p>
      {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13 }}>{zhEn("銷量載入失敗：", "Couldn't load sales: ")}{err}{zhEn("（只按庫存警戒線計）", " (using the stock alert levels only)")}</p>}
      {madeList.length > 0 && (
        <Card style={{ padding: 12, marginBottom: 10, background: C.greenSoft, borderColor: "transparent" }}>
          <p style={{ margin: 0, fontWeight: 900, color: "#047857", fontSize: 14 }}>{zhEn("已建立", "Created")} {madeList.length} {zhEn("張草稿", "drafts")}</p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
            {madeList.map(r => <Btn key={r.id} kind="ghost" small onClick={() => onOpenPo(r.id)}>{poNo(r.number)} <ChevronRight size={14} /></Btn>)}
          </div>
        </Card>
      )}
      {groups.length === 0 ? <Empty icon="🎉" title={zhEn("暫時唔使補貨", "Nothing to reorder right now")} sub={zhEn("所有追蹤庫存嘅貨品都夠賣，或者已經落咗單", "Every tracked item has enough stock, or is already on order")} />
        : groups.map(gr => {
          const items = gr.items;
          const on = items.filter(it => get(it).on);
          const amt = round2(on.reduce((s, it) => s + get(it).qty * it.p.cost, 0));
          const units = on.reduce((s, it) => s + get(it).qty, 0);
          const k = gr.key || "_";
          return (
            <Card key={k} style={{ padding: 12, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                {gr.sup ? <Truck size={18} color={C.accent} /> : <ClipboardList size={18} color={C.sub} />}
                <span style={{ flex: 1, minWidth: 0, fontWeight: 900, fontSize: 15, color: C.ink }}>{gr.sup?.name || zhEn("未指定供應商", "No supplier")}</span>
                <Pill tone="grey">{gr.lead} {zhEn("日到貨", "days to deliver")}</Pill>
              </div>
              {gr.scaled && <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 800, color: C.amber }}>{zhEn("已按最低訂貨", "Scaled up to meet the minimum order ")}{num(gr.sup?.moq_amount) ? zhEn("額 ", "amount ") + money(gr.sup.moq_amount, sym) : ""}{num(gr.sup?.moq_qty) ? zhEn(" / 量 ", " / quantity ") + qtyTxt(gr.sup.moq_qty) + zhEn(" 件", " items") : ""} {zhEn("按比例加大數量（最多 3 倍）", " by increasing quantities proportionally (up to 3×)")}</p>}
              <div style={{ display: "grid", gridTemplateColumns: wide ? "repeat(2,minmax(0,1fr))" : "minmax(0,1fr)", columnGap: 14 }}>
                {items.map(it => {
                  const s = get(it);
                  const dl = it.daysLeft === Infinity ? null : Math.floor(it.daysLeft);
                  return (
                    <div key={it.p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid " + C.line, opacity: s.on ? 1 : 0.45 }}>
                      <button type="button" onClick={() => setItem(it.p.id, { on: !s.on })} aria-label={zhEn("揀選", "Select")} style={{ border: "none", background: "none", padding: 2, cursor: "pointer", color: s.on ? C.accent : C.faint }}>
                        {s.on ? <CheckSquare size={22} /> : <Square size={22} />}
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 13.5, fontWeight: 900, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.p.name}</p>
                        <p style={{ margin: 0, fontSize: 11.5, fontWeight: 800, color: C.sub }}>
                          {zhEn("存", "Stock")} <span style={{ color: it.p.stock <= 0 ? C.red : it.p.stock <= it.p.lowStock ? C.amber : C.ink }}>{it.p.stock}</span>
                          {zhEn(" · 30日賣 ", " · sold in 30 days ") + qtyTxt(it.sold)}
                          {dl !== null && <span style={{ color: dl <= gr.lead ? C.red : C.amber }}>{zhEn(" · 約 ", " · about ") + dl + zhEn(" 日賣晒", " days left")}</span>}
                        </p>
                      </div>
                      <Stepper value={s.qty} min={1} onChange={v => setItem(it.p.id, { qty: v })} />
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 800, color: C.sub }}>{on.length} {zhEn("項 ·", "items ·")} {qtyTxt(units)} {zhEn("件 · 約", "units · about")} {money(amt, sym)}</span>
                <Btn small disabled={!on.length || !!busy || !!made[k]} onClick={() => create(gr)}>
                  {made[k] ? zhEn("已建立", "Created") : busy === k ? zhEn("建立中…", "Creating…") : zhEn("建立草稿採購單", "Create draft purchase order")}
                </Btn>
              </div>
            </Card>
          );
        })}
    </div>
  );
}

// ── A4 purchase order print (hidden iframe, like posReceipt.js) ─────────────
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
export function printPO(po, supplier, productsById, tenant, sym = "$") {
  const lines = sortLines(po);
  const t = rawTotals(po);
  const codeOf = (l) => (supplier && productsById?.[l.product_id]?.supplierCodes?.[supplier.id]) || "";
  const hasCode = lines.some(l => codeOf(l));
  const f = (n) => esc(money(n, sym));
  const rows = lines.map((l, i) => `<tr><td>${i + 1}</td>${hasCode ? `<td>${esc(codeOf(l))}</td>` : ""}<td>${esc(l.name)}${l.sku ? `<div class="m">${esc(l.sku)}</div>` : ""}</td>
    <td class="r">${qtyTxt(l.qty)}</td><td class="r">${num(l.free_qty) ? qtyTxt(l.free_qty) : ""}</td><td class="r">${f(l.cost)}</td>
    <td class="r">${num(l.discount) ? "-" + f(l.discount) : ""}</td><td class="r">${f(Math.max(0, num(l.cost) * num(l.qty) - num(l.discount)))}</td></tr>`).join("");
  const sumRow = (k, v, cls = "") => `<tr class="${cls}"><td>${esc(k)}</td><td class="r">${v}</td></tr>`;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(poNo(po.number))}</title><style>
  @page { size: A4; margin: 14mm } body { font: 12px/1.5 -apple-system, "PingFang HK", "Noto Sans TC", sans-serif; color: #111; margin: 0 }
  h1 { font-size: 20px; margin: 0 } h2 { font-size: 22px; margin: 0; text-align: right; letter-spacing: 1px } .m { color: #666; font-size: 11px }
  .top { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #111; padding-bottom: 10px; margin-bottom: 14px }
  .cols { display: flex; gap: 24px; margin-bottom: 14px } .box { flex: 1; border: 1px solid #ddd; border-radius: 6px; padding: 8px 10px }
  .box b { display: block; font-size: 11px; color: #666; margin-bottom: 2px } table { width: 100%; border-collapse: collapse }
  .items th { text-align: left; font-size: 11px; color: #555; border-bottom: 1px solid #111; padding: 5px 4px } .items td { padding: 6px 4px; border-bottom: 1px solid #eee; vertical-align: top }
  .r { text-align: right; white-space: nowrap } .sum { width: 260px; margin: 12px 0 0 auto } .sum td { padding: 3px 4px } .tot td { font-size: 15px; font-weight: 800; border-top: 1px solid #111 }
  .notes { margin-top: 16px; border-top: 1px dashed #aaa; padding-top: 8px } .sign { margin-top: 48px; display: flex; gap: 40px } .sign div { flex: 1; border-top: 1px solid #111; padding-top: 4px; font-size: 11px; color: #555 }
  </style></head><body>
  <div class="top"><div><h1>${esc(tenant?.name || "")}</h1>${tenant?.address ? `<div class="m">${esc(tenant.address)}</div>` : ""}${tenant?.phone ? `<div class="m">電話 ${esc(tenant.phone)}</div>` : ""}</div>
  <div><h2>採購單</h2><div class="r"><b>${esc(poNo(po.number))}</b></div><div class="r m">${esc(PO_STATUS[po.status]?.[0] || "")}</div></div></div>
  <div class="cols"><div class="box"><b>供應商</b>${supplier ? `${esc(supplier.name)}${supplier.contact ? `<br>${esc(supplier.contact)}` : ""}${supplier.phone ? `<br>${esc(supplier.phone)}` : ""}${supplier.email ? `<br>${esc(supplier.email)}` : ""}${supplier.address ? `<br>${esc(supplier.address)}` : ""}` : "—"}</div>
  <div class="box"><b>落單日期</b>${esc(po.order_date || "")}<b style="margin-top:6px">預計到貨</b>${esc(po.expected_date || "—")}${po.supplier_ref ? `<b style="margin-top:6px">供應商單號</b>${esc(po.supplier_ref)}` : ""}</div></div>
  <table class="items"><thead><tr><th>#</th>${hasCode ? "<th>供應商編號</th>" : ""}<th>貨品</th><th class="r">數量</th><th class="r">贈品</th><th class="r">單價</th><th class="r">折扣</th><th class="r">金額</th></tr></thead><tbody>${rows}</tbody></table>
  <table class="sum">${sumRow("小計", f(t.subtotal))}${t.discount ? sumRow("折扣" + (po.discount_type === "percent" ? ` ${num(po.discount)}%` : ""), "-" + f(t.discount)) : ""}${t.tax ? sumRow(`稅 ${num(po.tax_rate)}%`, f(t.tax)) : ""}${t.shipping ? sumRow("運費", f(t.shipping)) : ""}${sumRow("合計 (HKD)", f(po.total), "tot")}</table>
  ${po.notes ? `<div class="notes"><b>備註：</b>${esc(po.notes)}</div>` : ""}
  <div class="sign"><div>訂購人簽署</div><div>供應商確認</div></div>
  </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* blocked */ } setTimeout(() => iframe.remove(), 60000); }, 250);
}
